<template>
  <div class="flex w-full h-full relative">
    <Line
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { cumulativeSum, roundDecimals } from '@/helpers';
import { t } from '@/i18n';
import Project from '@/models/Project';
import Trade from '@/models/Trade';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import { Line } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  project: Project | null;
  trades: Trade[];
}>();

// Store for baseline overrides
const baselineOverrides = ref<Map<number, number[]>>(new Map());
// Chart configuration
const chartId = 'baseline-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const calculateBaselineData = computed(() => {
  if(!props.project) return [];

  const monthsInProject = props.project.contractMonths;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    props.trades.filter((trade) => {
      return trade.baseline_start_date && trade.baseline_end_date && trade.baseline_forecast;
    }).forEach((trade) => {
      if(trade.baselineStart <= projectMonth && projectMonth <= trade.baselineEnd) {
        const monthIndex = Math.round(Math.abs(trade.baselineStart.diff(projectMonth, ['months']).months));
        // Use override if available, otherwise use original baseline
        const override = baselineOverrides.value.get(trade.id);

        const baselineValue = override ?
          (override[monthIndex] ?? 0) :
          (trade.baseline_forecast[monthIndex] ?? 0);

        monthlyTotals[i] += roundDecimals(baselineValue);
      }
    });
  });

  return cumulativeSum(monthlyTotals);
});

const chartData = computed(() => {
  if(!props.project) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const contractLength = props.project.contractLength;

  const labels = Array.from({ length: contractLength }, (_, i) => {
    const date = DateTime.fromJSDate(props.project.contractStartDate).startOf('month');

    return date.plus({ months: i }).toFormat('MMM-yyyy');
  });

  return {
    labels,
    datasets: [
      {
        label: t('HeadContract.CashflowBaseline.BaslineChart.baselineChartLegend'),
        data: calculateBaselineData.value,
        fill: false,
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#EF4444',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        order: 2,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        padding: 8,
        font: {
          size: 12,
          weight: '600',
        },
      },
    },

    title: {
      display: false,
    },
  },

  maintainAspectRatio: false,
  scales: {
    y: {
      ticks: {},
    },
  },
}));

// Expose methods for parent component
const updateBaselineOverride = (tradeToOverride: Trade, newBaselines: number[]) => {
  baselineOverrides.value.set(tradeToOverride.id, newBaselines);
};

const clearBaseline = (trade: Trade) => {
  baselineOverrides.value.delete(trade.id);
};

watch([() => props.project?.id], (newValue, oldValue) => {
  if(newValue !== oldValue) {
    baselineOverrides.value.clear();
  }
}, { immediate: true });

defineExpose({
  updateBaselineOverride,
  clearBaseline,
});
</script>

<style scoped></style>
